import { FC, useEffect, useCallback, useState } from 'react'
import { useForm, Controller, FieldError } from 'react-hook-form'
import { Stack, TextField, Typography } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import { AxiosError } from 'axios'
import isNil from 'lodash.isnil'
import { useNavigate } from 'react-router-dom'

import { SidePanelElementProps, useSidePanelContext } from '@clara/layout-next'
import { Banner, Button } from '@clara/ui'
import { useT } from '@clara/locale'
import { isEmpty } from '@clara/utils'
import { AsyncState, useAsyncFn } from '@clara/hooks'

import { getErrorHelperText } from '@/utils/form'
import { OPERATION_COUNTRY } from '@/config/env'
import { useSnackbar } from '@/utils/use-snackbar'
import { useDebounce } from '@/utils/use-debounce'

import { getBankInfoSchema } from '../use-bankinfo-schema'
import { getBankNameService } from '../../../api/get-bank-name'
import {
  BankInfo,
  BankInfoResponse,
  postBankInfo,
  UserInfoResponse,
} from '../../../api/bank-info'
import { useReimbursementsInProgress } from '@/features/reimbursements/api/use-reimbursements-inprogress'

type Props = {
  getBankInfo: () => Promise<BankInfoResponse>
  bankInfo?: BankInfoResponse
  userInfoResponse?: AsyncState<UserInfoResponse>
  isManagerOrCompanyOwner?: boolean
}

export const MxBankInfoForm: FC<SidePanelElementProps & Props> = ({
  getBankInfo,
  bankInfo,
  userInfoResponse,
  isManagerOrCompanyOwner,
}) => {
  const { t } = useT('reimbursements')
  const sidePanel = useSidePanelContext()
  const navigate = useNavigate()
  const schema = getBankInfoSchema(t)
  const showSnackbar = useSnackbar()
  const [saveButtonIsLoading, setSaveButtonIsLoading] = useState(false)
  const [bankNameNotFound, setBankNameNotFound] = useState(false)

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid, isDirty },
  } = useForm({
    defaultValues: {
      name: userInfoResponse?.value?.name ?? '',
      rfc: bankInfo?.taxPayerId,
      clabe: bankInfo?.accountNumber,
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  const [bankNameResponse, getBankName] = useAsyncFn(async (clabe: string) => {
    setBankNameNotFound(false)
    return await getBankNameService({ clabe })
  }, [])

  const clabe = watch('clabe')
  const debouncedClabe = useDebounce<string>(clabe)

  useEffect(() => {
    if (debouncedClabe && debouncedClabe.length === 18) {
      getBankName(debouncedClabe)
    }
  }, [debouncedClabe, getBankName])

  useEffect(() => {
    setBankNameNotFound(false)
    if (!bankNameResponse.loading && bankNameResponse.error) {
      if ((bankNameResponse.error as AxiosError).response?.status === 417) {
        setBankNameNotFound(true)
      } else {
        showSnackbar(t('common:helpCenter.panel.errorMessage'), {
          variant: 'error',
        })
      }
    } else if (bankNameResponse.value && !bankNameResponse.value?.name) {
      setBankNameNotFound(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankNameResponse])

  const updateBankInfo = useCallback(
    async (data: { name: string; clabe: string; rfc: string }) => {
      const payload: BankInfo = {
        taxPayerId: data.rfc,
        accountNumber: data.clabe,
        countryCode: OPERATION_COUNTRY,
      }
      setSaveButtonIsLoading(true)
      try {
        const res = await postBankInfo(payload, bankInfo?.uuid)
        if (!isNil(res)) {
          showSnackbar(t('bankinfo.update.success'), {
            variant: 'success',
          })
          sidePanel.onClose()
          getBankInfo().then(() => {
            if (isManagerOrCompanyOwner) {
              navigate('/reimbursements/create')
            }
          })
        }
      } catch (error) {
        // TODO: manage endpoint error codes
        showSnackbar(t('bankinfo.update.error'), {
          variant: 'error',
        })
      } finally {
        setSaveButtonIsLoading(false)
      }
    },
    [
      bankInfo?.uuid,
      showSnackbar,
      t,
      sidePanel,
      getBankInfo,
      isManagerOrCompanyOwner,
      navigate,
    ],
  )

  const { onSubmit } = useReimbursementsInProgress(updateBankInfo)

  const resolveClabeHelperText = useCallback(
    (error: FieldError | undefined) => {
      if (error) return getErrorHelperText(t, error)
      if (bankNameNotFound) return t('bankinfo.clabe.bank_not_found')
      return t('bankinfo.sidepanel.clabe.helpertext')
    },
    [t, bankNameNotFound],
  )

  return (
    <Stack>
      {isManagerOrCompanyOwner && !bankInfo && (
        <Typography
          variant="body1"
          sx={(theme) => ({
            color: theme.palette.v3.content.medium_emphasis,
            marginBottom: theme.spacing(5),
          })}
        >
          {t('bankinfo.sidepanel.coandmanagertitle')}
        </Typography>
      )}
      <Typography
        variant="body1"
        sx={(theme) => ({
          color: theme.palette.v3.content.medium_emphasis,
          marginBottom: theme.spacing(3),
        })}
      >
        {t('bankinfo.sidepanel.description')}
      </Typography>

      {bankInfo &&
        ['INVALID', 'ERROR_STP', 'STP_ACCOUNT'].includes(
          bankInfo.validationStatus,
        ) && (
          <Banner
            data-testid="limit_date_close_alert"
            status="info"
            sx={{ borderRadius: '8px', mb: 3 }}
          >
            {
              {
                INVALID: t('bankinfo.alert.invalid'),
                ERROR_STP: t('bankinfo.alert.stp-error'),
                STP_ACCOUNT: t('bankinfo.alert.stp-account'),
              }[bankInfo.validationStatus]
            }
          </Banner>
        )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction={'column'} gap={3} mb={7}>
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t('bankinfo.sidepanel.name.label')}
                variant="outlined"
                error={error != null}
                helperText={error && getErrorHelperText(t, error)}
                size="small"
                fullWidth
                value={userInfoResponse?.value?.name ?? ''}
                disabled={userInfoResponse?.loading}
                InputProps={{
                  readOnly: true,
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="rfc"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t('bankinfo.sidepanel.rfc.label')}
                variant="outlined"
                error={error != null}
                helperText={error && getErrorHelperText(t, error)}
                size="small"
                fullWidth
                disabled={false}
              />
            )}
          />
          <Controller
            control={control}
            name="clabe"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t('bankinfo.sidepanel.clabe.label')}
                variant="outlined"
                error={error != null || bankNameNotFound}
                helperText={resolveClabeHelperText(error)}
                size="small"
                fullWidth
                disabled={false}
              />
            )}
          />
          <TextField
            value={bankNameResponse.value?.name ?? ''}
            label={t('bankinfo.sidepanel.bank.label')}
            variant="outlined"
            size="small"
            fullWidth
            disabled={isEmpty(bankNameResponse.value)}
            InputProps={{
              readOnly: true,
            }}
          />

          <Stack
            direction="row"
            gap={2}
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'center',
              position: 'absolute',
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(3),
              left: theme.spacing(3),
              right: theme.spacing(3),
              bottom: 0,
              backgroundColor: theme.palette.common.white,
              zIndex: 1,
            })}
          >
            <Button
              type={'submit'}
              sx={{ width: '208px' }}
              isLoading={bankNameResponse.loading || saveButtonIsLoading}
              isDisabled={
                isEmpty(bankNameResponse.value) || !isValid || !isDirty
              }
            >
              {t('bankinfo.sidepanel.save')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Stack>
  )
}
